import React, { useContext, useState, useEffect, useRef } from "react";
import { UserStateContext } from "../../../components/context/user-state-context.js";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { PageHeaderMui } from "../../../components/index.js";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  CallMadeIcon,
  CheckCircleIcon,
  CloseIcon,
  DataFieldsIcon,
  ElevationIcon,
  MonetizationOnIcon,
  OpenFullIcon,
  OpenInNewWindowIcon,
  SendIcon,
  ShieldPersonIcon,
  SMSOutgoingIcon,
  StatusDotIcon,
} from "../../../components/mui/index.js";
import TextField from "@mui/material/TextField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { DatePicker } from "@mui/x-date-pickers";
import EnhancedTable from "../../../components/mui/enhanced-table/enhanced-table.component.js";
import StackedSnackbar from "../../../components/mui/snackbar/StackedSnackbar.js";
import { SingleSelectFilter } from "../../../components/mui/filter/single-select.component.js";
import {
  getQBInvoices,
  refreshCacheForQBInvoices,
} from "../../../shared/billing.api.js";
import { Loader } from "../../../components/loader/loader.component.js";
import { QBInvoiceDetailModal } from "./detail-modal.component.js";
import {
  useBulkInvoiceActions,
} from "./actions/bulk-actions.component.js";
import { useSingleInvoiceActions } from "./actions/single-action.component.js";

import {
  generateLogsTooltip,
  generateTotalAmountTooltip,
  generateSmsOverageTooltip,
  determineInvoiceStatus,
  generateContactOverageTooltip,
} from "./helpers/tooltips.component.js";
import { OpenInActions } from "./helpers/open-in-actions.component.js";
import { OverageSinglePopper } from "./helpers/overage-single-popper.js";
import { OverageBulkPopper } from "./helpers/overage-bulk-popper.js";
import { ApiStatusBox } from "../tools/api-status-box.component.js";
import { CacheStatusBox } from "../tools/cache-status-box.component.js";
import { SFOveragesModal } from "./sf-overages-modal.component.js";
import CONSTANTS from "../../../shared/constants.js";
import Popper from "@mui/material/Popper";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Badge from "@mui/material/Badge";

dayjs.extend(relativeTime);

export const QuickBooksInvoiceList = () => {
  const { user } = useContext(UserStateContext);
  // Default to current month
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [renderIteration, setRenderIteration] = useState(0);
  const [errors, setErrors] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const snackbarRef = useRef(null);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(null);
  const [metaData, setMetaData] = useState(null);
  const [overagesModalOpen, setOveragesModalOpen] = useState(false);
  const [fieldsPopperAnchor, setFieldsPopperAnchor] = useState(null);
  // Selection is held here rather than inside the table so a finished bulk
  // action can leave only the invoices it failed on ticked. These are charges:
  // re-selecting everything would put a second charge one stray click away,
  // and what the user actually needs next is the subset that did not go
  // through. The table prunes whatever is no longer on screen.
  const [selectedInvoices, setSelectedInvoices] = useState([]);

  // Use bulk logic from bulk file
  const bulk = useBulkInvoiceActions({
    renderIteration,
    setRenderIteration,
    snackbarRef,
  });
  const single = useSingleInvoiceActions({
    renderIteration,
    setRenderIteration,
    snackbarRef,
    setIsLoading,
  });

  const filterOptions = [
    {
      component: SingleSelectFilter,
      label: "Invoice Status",
      parentToolTip:
        "Filter invoices based on their current status in the billing workflow",
      key: "invoice_status",
      defaultValue: "true",
      isDefault: true,
      options: [
        {
          label: "Show all",
          value: "all",
          labelTooltip: "Show all quickbooks invoices for the selected month",
          isDefault: true,
        },
        {
          label: "Fresh Invoice",
          labelTooltip:
            "No changes have been made to this invoice by Cinch Ops since it was created",
          value: "fresh",
        },
        {
          label: "Updated Invoice",
          labelTooltip:
            "Cinch Ops has made changes to this invoice (such as SMS overage updates) but it has not yet been stripe billed, mnarked as paid, or sent to the customer",
          value: "updated_invoice",
        },

        {
          label: "Stripe Billed to Customer",
          labelTooltip:
            "Invoice has been charged to the customer's credit card via Stripe but not yet marked as paid or sent in QB",
          value: "stripe_billed",
        },
        {
          label: "Marked as Paid in QuickBooks",
          labelTooltip:
            "Invoice has been marked as fully paid in QuickBooks, but not yet sent to the customer",
          value: "qb_paid",
        },

        {
          label: "Invoice sent to customer",
          labelTooltip:
            "Invoice has been sent to the customer via email (Complete)",
          value: "sent_to_customer",
        },
      ],
    },

    {
      component: SingleSelectFilter,
      label: "SMS Overage Status",
      parentTooltip:
        "Filter invoices based on whether they have SMS overages values for this month",
      key: "sms_overage_status",
      defaultValue: "all",
      isDefault: true,
      options: [
        {
          label: "Show all",
          value: "all",
          isDefault: true,
          labelTooltip:
            "Show all quickbooks invoices regardless of SMS overage amounts",
        },
        {
          label: "With SMS Overage",
          labelTooltip:
            "Show only invoices that have SMS overage values above zero for this month",
          value: "with_overage",
        },
        {
          label: "Without SMS Overage",
          labelTooltip:
            "Show only invoices that do not have any SMS overage values (0) for this month",
          value: "without_overage",
        },
      ],
    },

    {
      component: SingleSelectFilter,
      label: "Contact Overage Status",
      parentTooltip:
        "Filter invoices based on whether they have Contact overages values for this month",
      key: "contact_overage_status",
      defaultValue: "all",
      isDefault: true,
      options: [
        {
          label: "Show all",
          value: "all",
          isDefault: true,
          labelTooltip:
            "Show all quickbooks invoices regardless of Contact overage amounts",
        },
        {
          label: "With Contact Overage",
          labelTooltip:
            "Show only invoices that have Contact overage values above zero for this month",
          value: "with_overage",
        },
        {
          label: "Without Contact Overage",
          labelTooltip:
            "Show only invoices that do not have any Contact overage values (0) for this month",
          value: "without_overage",
        },
      ],
    },

    {
      component: SingleSelectFilter,
      label: "Ready for Automated Billing",
      parentTooltip:
        "Filter invoices based on whether they are ready to be automatically billed to the customer",
      key: "ready_for_billing",
      defaultValue: "all",
      isDefault: true,
      options: [
        {
          label: "Show all",
          value: "all",
          isDefault: true,
          labelTooltip:
            "Show all quickbooks invoices regardless of ready for billing status",
        },
        {
          label: "Ready for Automated Billing",
          labelTooltip:
            "Show only invoices that are ready to be automatically billed to the customer",
          value: "ready",
        },
        {
          label: "Not Ready for Automated Billing",
          labelTooltip:
            "Show only invoices that are not ready to be automatically billed to the customer. These may be missing account, customer, or payment information",
          value: "not_ready",
        },
        {
          label: "No Account",
          labelTooltip:
            "Show only invoices that are missing linked Cinch Ops Account information",
          value: "no_account",
        },
        {
          label: "No Stripe Customer ID",
          labelTooltip:
            "Show only invoices that are missing linked Stripe Customer ID information",
          value: "no_stripe_customer_id",
        },
      ],
    },
    {
      component: SingleSelectFilter,
      label: "Errors",
      parentTooltip:
        "Filter invoices based on whether they have logged errors in their changelog",
      key: "has_errors",
      defaultValue: "all",
      isDefault: true,
      options: [
        {
          label: "Show all",
          value: "all",
          isDefault: true,
          labelTooltip:
            "Show all quickbooks invoices regardless of error status",
        },
        {
          label: "With Errors",
          labelTooltip:
            "Show only invoices that have logged errors in their changelog",
          value: "with_errors",
        },
        {
          label: "Without Errors",
          labelTooltip:
            "Show only invoices that do not have any logged errors in their changelog",
          value: "without_errors",
        },
      ],
    },

    {
      component: SingleSelectFilter,
      label: "Hide Mailer Only Invoices",
      parentTooltip:
        "Filter invoices based on whether they have logged errors in their changelog",
      key: "hide_mailer_only_invoices",
      defaultValue: "true",

      options: [
        {
          label: "Hide Mailer Only Invoices",
          value: "true",
          isDefault: true,
          labelTooltip:
            " Hide invoices that were created for mailer purposes only, eg. invoices that only have mailer charges ",
        },
        {
          label: "Include Mailer Only Invoices",

          labelTooltip:
            "Include invoices that were created for mailer purposes only, eg. invoices that only have mailer charges ",
          value: "false",
        },
      ],
    },
  ];

  const [filters, setFilters] = React.useState({});

  const handleRowClick = (row) => {
    setInvoiceModalOpen(row.invoice.Id);
  };

  const headCells = [
    {
      key: "DocNumber",
      label: "QB DocNum",
      width: "6%",
      displayComponent: ({ data }) => (
        <Tooltip
          placement="right"
          title={
            <Typography>QB Invoice Id: {data?.invoice?.Id || "--"}</Typography>
          }
        >
          <Typography sx={{}}>{data?.invoice?.DocNumber || "--"}</Typography>
        </Tooltip>
      ),
    },
    {
      key: "TxnDate",
      label: "Invoice Date",
      width: "10%",
      displayComponent: ({ data }) => (
        <Tooltip
          placement="right"
          title={
            <Typography sx={{}}>
              Created Date:
              {data?.invoice?.MetaData?.CreateTime
                ? dayjs(data.invoice?.MetaData?.CreateTime).format(
                    "MMM D, YYYY",
                  )
                : "--"}
            </Typography>
          }
        >
          <Typography sx={{}}>
            {data?.invoice?.TxnDate
              ? dayjs(data.invoice?.TxnDate).format("MMM D, YYYY")
              : "--"}
          </Typography>
        </Tooltip>
      ),
    },
    {
      key: "CustomerRefName",
      label: "Company Name",
      width: "15%",
      displayComponent: ({ data }) => (
        <Typography sx={{ fontWeight: "bold" }}>
          {data?.invoice?.CustomerRef?.name
            ? data.invoice.CustomerRef.name
            : "--"}
        </Typography>
      ),
    },
    {
      key: "accountName",
      label: "Linked Account",
      width: "10%",
      displayComponent: ({ data }) => (
        <Typography sx={{}}>
          {!!data?.accountName ? `${data.accountName}` : "--"}
        </Typography>
      ),
    },
    {
      key: "readyForBilling",
      label: "Ready for Billing",
      width: "10%",
      displayComponent: ({ data }) => {
        const readyForBilling =
          !!data?.accountName &&
          !!data?.contract_third_party_info?.stripe_customer_id;
        const complete = data.invoice.EmailStatus === "EmailSent";

        return (
          <>
            {complete ? (
              <Tooltip title={<Typography>Invoice Sent</Typography>}>
                <span>
                  <StatusDotIcon fill={"#5d8ef8ff"} />{" "}
                </span>
              </Tooltip>
            ) : readyForBilling ? (
              <Tooltip
                title={<Typography>Ready for Automated Billing</Typography>}
              >
                <span>
                  <StatusDotIcon fill={"#3bb143"} />{" "}
                </span>
              </Tooltip>
            ) : (
              <Tooltip
                title={
                  <Stack>
                    <Typography>Not Ready for Automated Billing.</Typography>
                    {!data.accountName && (
                      <Typography>- Missing linked Cinch Account</Typography>
                    )}
                    {!data?.contract_third_party_info.stripe_customer_id && (
                      <Typography>- Missing Stripe Customer ID</Typography>
                    )}
                  </Stack>
                }
              >
                <span>
                  <StatusDotIcon fill={"#ff0000"} />{" "}
                </span>
              </Tooltip>
            )}
          </>
        );
      },
    },
    {
      key: "smsOverage",
      label: "SMS Overage",
      width: "10%",
      displayComponent: ({ data }) => (
        <Tooltip title={generateSmsOverageTooltip(data, metaData)}>
          <Typography sx={{}}>
            {data?.overages_and_usage?.usage_sms_credits?.current
              ?.overage_usage || "--"}
          </Typography>
        </Tooltip>
      ),
    },
    {
      key: "contactOverage",
      label: "Contact Overage",
      width: "10%",
      displayComponent: ({ data }) => (
        <Tooltip title={generateContactOverageTooltip(data, metaData)}>
          <Typography sx={{}}>
            {data?.overages_and_usage?.additional_usage_base_contacts?.current
              ?.overage_usage || "--"}
          </Typography>
        </Tooltip>
      ),
    },
    {
      key: "TotalAmt",
      label: "Total Invoice Amount",
      width: "10%",
      displayComponent: ({ data }) => (
        <Tooltip title={generateTotalAmountTooltip(data)}>
          <Typography sx={{}}>
            {data.invoice?.TotalAmt
              ? `$${data.invoice.TotalAmt.toFixed(2)}`
              : "--"}
          </Typography>
        </Tooltip>
      ),
    },
    {
      noSort: true,
      key: "logs",
      label: "Logs",
      width: "10%",
      displayComponent: ({ data }) => (
        <>
          {data.changelogs && data.changelogs.length > 0 ? (
            <Tooltip title={generateLogsTooltip(data.changelogs)}>
              <Typography>
                {data.changelogs.length} log
                {data.changelogs.length > 1 ? "s" : ""}
              </Typography>
            </Tooltip>
          ) : (
            <Tooltip
              title={
                <Typography>
                  This either means we haven't attempted to make any changes to
                  this invoice, or if some error is obvious, then not we're
                  logging that error
                </Typography>
              }
            >
              <Typography sx={{}}> No logs found </Typography>
            </Tooltip>
          )}
        </>
      ),
    },
    {
      key: "EmailStatus",
      label: "Invoice Status",
      width: "10%",
      displayComponent: ({ data }) => determineInvoiceStatus(data),
    },
    {
      key: "actions",
      label: "Actions",
      width: "5%",
      noSort: true,
      displayComponent: ({ data }) => (
        <>
          <OpenInActions data={data} />

          <OverageSinglePopper
            data={data}
            snackbarRef={snackbarRef}
            onActionComplete={(result) => {
              if (result) {
                handleRecentlyUpdatedInvoices([data.invoice.Id]);
              }
            }}
          />

          <Tooltip
            title={
              <Typography>
                {!data.accountId
                  ? "Please Link account first"
                  : !data?.contract_third_party_info?.stripe_customer_id
                    ? "No stripe customer id found"
                    : "Charge Stripe Customer for this invoice"}{" "}
              </Typography>
            }
          >
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                if (!data.accountId) {
                  snackbarRef.current?.addSnackbar({
                    type: "error",
                    message: `Please link an account to QuickBooks Invoice ${data.invoice?.Id} before charging customer`,
                  });
                  return;
                }
                if (!data?.contract_third_party_info?.stripe_customer_id) {
                  snackbarRef.current?.addSnackbar({
                    type: "error",
                    message: `No Stripe Customer ID found for QuickBooks Invoice ${data.invoice?.Id}. Please ensure the linked account has a valid Stripe Customer ID before charging.`,
                  });
                  return;
                }
                single.openChargeCustomerModal(data, (result) => {
                  if (result) {
                    handleRecentlyUpdatedInvoices([data.invoice.Id]);
                  }
                });
              }}
            >
              <MonetizationOnIcon />
            </IconButton>
          </Tooltip>

          <Tooltip
            title={<Typography>Mark quickbooks invoice as paid</Typography>}
          >
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                single.openMarkPaidModal(data, (result) => {
                  if (result) {
                    handleRecentlyUpdatedInvoices([data.invoice.Id]);
                  }
                });
              }}
            >
              <CheckCircleIcon />
            </IconButton>
          </Tooltip>

          <Tooltip
            title={
              data.invoice.EmailStatus === "EmailSent" &&
              data.changelogs?.find(
                (log) =>
                  log.result_status === "success" &&
                  log.action.includes("send_customer_invoice"),
              ) ? (
                <Typography>Invoice already sent to customer</Typography>
              ) : (
                <Typography>Send this invoice to the customer</Typography>
              )
            }
          >
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                /*      if (!data.accountId) {
                  snackbarRef.current?.addSnackbar({
                    type: "error",
                    message: `Please link an account to QuickBooks Invoice ${data.invoice?.Id} before sending to customer`,
                  });
                  return;
                } */
                if (
                  data.invoice.EmailStatus === "EmailSent" &&
                  data.changelogs?.find(
                    (log) =>
                      log.result_status === "success" &&
                      log.action.includes("send_customer_invoice"),
                  )
                ) {
                  snackbarRef.current?.addSnackbar({
                    type: "info",
                    message: `Invoice ${data.invoice?.Id} already has been sent to the customer. Not sending again.`,
                  });
                  return;
                }
                single.openSendInvoiceModal(data, (result) => {
                  if (result) {
                    handleRecentlyUpdatedInvoices([data.invoice.Id]);
                  }
                });
              }}
            >
              <SendIcon />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  const QBInvoicesApiWrapper = () => {
    return (params) =>
      getQBInvoices({
        start_date: dayjs(selectedMonth).startOf("month").format("YYYY-MM-DD"),
        ...params,
      });
  };

  const actionButtons = {
    displayComponent: ({ data }) => (
      <Stack direction="row" spacing={2}>
        {data.selected?.length > 0 && (
          <OverageBulkPopper
            data={data.selected}
            snackbarRef={snackbarRef}
            didActionsOccur={(attempted) => {
              if (attempted) {
                handleRecentlyUpdatedInvoices(
                  data.selected.map((i) => i.invoice.Id),
                );
              }
            }}
          />
        )}

        {data.selected?.length > 0 && (
          <Tooltip
            title={
              <Typography>
                Charge selected invoices to Stripe customers
              </Typography>
            }
          >
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                bulk.handleBulkChargeStripeCustomers(
                  data.selected,
                  (attempted) => {
                    if (attempted) {
                      handleRecentlyUpdatedInvoices(
                        data.selected.map((i) => i.invoice.Id),
                      );
                    }
                  },
                );
              }}
            >
              <MonetizationOnIcon />
            </IconButton>
          </Tooltip>
        )}

        {data.selected?.length > 0 && (
          <Tooltip
            title={<Typography>Mark selected invoices as paid</Typography>}
          >
            <IconButton
              sx={{
                ":hover": {
                  backgroundColor: "#DAEFFF",
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                bulk.handleBulkMarkInvoicesPaid(
                  data.selected,
                  (attempted) => {
                    if (attempted) {
                      handleRecentlyUpdatedInvoices(
                        data.selected.map((i) => i.invoice.Id),
                      );
                    }
                  },
                );
              }}
            >
              <CheckCircleIcon />
            </IconButton>
          </Tooltip>
        )}

        {data.selected?.length > 0 && (
          <Tooltip
            title={<Typography>Send selected invoices to customers</Typography>}
          >
            <IconButton
              sx={{
                ":hover": {
                  backgroundColor: "#DAEFFF",
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                bulk.handleBulkSendInvoicesToCustomers(
                  data.selected,
                  (attempted) => {
                    if (attempted) {
                      handleRecentlyUpdatedInvoices(
                        data.selected.map((i) => i.invoice.Id),
                      );
                    }
                  },
                );
              }}
            >
              <SendIcon />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    ),
  };

  const handleRecentlyUpdatedInvoices = (invoices) => {
    refreshCacheForQBInvoices({
      invoices: invoices,
      start_date: dayjs(selectedMonth).startOf("month").format("YYYY-MM-DD"),
    })
      .then((e) => {
        setRenderIteration(renderIteration + 1);
      })
      .catch((err) => {
        console.error("Error refreshing invoice cache:", err);
      });
  };

  // Use headCells to generate allFields for toggling
  const allFields = headCells.map(({ key, label }) => ({ key, label }));

  // Load visible fields from localStorage or default to all
  const LOCAL_STORAGE_KEY = "qb_invoice_list_visible_fields";
  const getInitialVisibleFields = () => {
    try {
      const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Only keep valid keys
        return allFields
          .map((f) => f.key)
          .filter((key) => parsed.includes(key));
      }
    } catch {}
    return allFields.map((f) => f.key);
  };

  const [visibleFields, setVisibleFields] = useState(getInitialVisibleFields);

  // Persist visibleFields to localStorage
  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(visibleFields));
  }, [visibleFields]);

  // Filter headCells based on visibleFields
  const filteredHeadCells = headCells.filter((cell) =>
    visibleFields.includes(cell.key),
  );

  return (
    <>
      <div className="wrapper">
        <PageHeaderMui
          type="QuickBooks Invoice List"
          icon={<ShieldPersonIcon />}
        />
        <Stack spacing={2}>
          <Box sx={{ px: "20px", pt: "20px" }}></Box>
          <div className="mui-wrapper">
            <Stack spacing={2} sx={{ p: "20px" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "space-between",
                  width: "100%",
                }}
              >
                <Stack spacing={3} sx={{ width: "fit-content" }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Month (Invoice Date)"
                      views={["month", "year"]}
                      disableFuture
                      value={selectedMonth}
                      onChange={(newValue) => {
                        setSelectedMonth(newValue);
                        setRenderIteration(renderIteration + 1);
                      }}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  </LocalizationProvider>
                </Stack>
                <Stack sx={{ ml: "auto" }}>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      setOveragesModalOpen(true);
                    }}
                    variant="outlined"
                  >
                    View this months overages
                  </Button>
                </Stack>

                <Stack direction={"row"} spacing={2} sx={{ ml: "auto" }}>
                  <CacheStatusBox
                    metaData={metaData}
                    setIsLoading={setIsLoading}
                    snackbarRef={snackbarRef}
                    setRenderIteration={setRenderIteration}
                    renderIteration={renderIteration}
                  />
                  {/* API Status Section */}
                  <ApiStatusBox snackbarRef={snackbarRef} />
                </Stack>
              </Box>
              <Box>
                {errors && (
                  <Stack spacing={1} sx={{ mb: 2, position: "relative" }}>
                    <Typography color="error">
                      Error loading QuickBooks Invoices:
                    </Typography>
                    <Typography color="error">
                      {JSON.stringify(errors)}
                    </Typography>
                    <Typography color="error">
                      {JSON.stringify(errors?.errors)}
                    </Typography>
                    <Typography color="error">
                      {JSON.stringify(errors?.response)}
                    </Typography>
                    <IconButton
                      size="small"
                      variant="outlined"
                      sx={{ position: "absolute", top: 0, right: 0 }}
                      onClick={() => setErrors(null)}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Stack>
                )}
              </Box>

              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "40px",
                  backgroundColor:
                    CONSTANTS.ENV === "PROD" ? "#ebb7b7ff" : "#83ec9eff",
                }}
              >
                {CONSTANTS.ENV === "LOCAL" || CONSTANTS.ENV === "DEV" ? (
                  <Typography sx={{ color: "#000000ff", fontWeight: "bold" }}>
                    You are in {CONSTANTS.ENV} environment
                  </Typography>
                ) : (
                  <Typography sx={{ color: "#000000ff", fontWeight: "bold" }}>
                    You are in PRODUCTION environment - Please exercise caution!
                  </Typography>
                )}
              </Box>

              <EnhancedTable
                type="QuickBooks Invoice (Supports Invoice Id, docnum, Account name, and Company name search) Searching will temprarily override any active filters"
                headCells={filteredHeadCells}
                enableCheckboxes
                api={QBInvoicesApiWrapper()}
                hidePagination
                actionButtons={actionButtons}
                errors={(e) => {
                  setErrors(e);
                }}
                apiMetaData={(meta) => {
                  setMetaData(meta);
                }}
                defaultSort={"Id"}
                renderIterationWithLoading={renderIteration}
                selectedState={[selectedInvoices, setSelectedInvoices]}
                filterOptions={filterOptions}
                filtersState={[filters, setFilters]}
                handleRowClick={(row) => {
                  handleRowClick(row);
                }}
                middleActions={
                  <>
                    <Tooltip title="Edit Fields">
                      <Badge
                        badgeContent={`${visibleFields.length}/${allFields.length}`}
                        color="primary"
                        sx={{ mr: 1 }}
                        anchorOrigin={{
                          vertical: "top",
                          horizontal: "right",
                        }}
                      >
                        <IconButton
                          sx={{
                            width: "40px",
                            height: "40px",
                            textAlign: "center",
                            padding: 0,
                            backgroundColor: "#fff",
                            ":hover": {
                              backgroundColor: "#DAEFFF",
                            },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setFieldsPopperAnchor(
                              fieldsPopperAnchor ? null : e.currentTarget,
                            );
                          }}
                        >
                          <DataFieldsIcon />
                        </IconButton>
                      </Badge>
                    </Tooltip>
                    <Popper
                      open={!!fieldsPopperAnchor}
                      anchorEl={fieldsPopperAnchor}
                      placement="bottom-start"
                      style={{ zIndex: 1300 }}
                    >
                      <ClickAwayListener
                        onClickAway={() => setFieldsPopperAnchor(null)}
                      >
                        <Box
                          sx={{
                            p: 2,
                            bgcolor: "background.paper",
                            boxShadow: 2,
                            borderRadius: 1,
                            minWidth: 220,
                          }}
                        >
                          <Typography sx={{ fontWeight: "bold", mb: 1 }}>
                            Toggle Fields
                          </Typography>
                          {allFields.map((field) => (
                            <FormControlLabel
                              key={field.key}
                              control={
                                <Checkbox
                                  checked={visibleFields.includes(field.key)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setVisibleFields((prev) => [
                                        ...prev,
                                        field.key,
                                      ]);
                                    } else {
                                      // Prevent hiding all fields
                                      if (visibleFields.length > 1) {
                                        setVisibleFields((prev) =>
                                          prev.filter((k) => k !== field.key),
                                        );
                                      }
                                    }
                                  }}
                                />
                              }
                              label={field.label}
                              sx={{ display: "block" }}
                            />
                          ))}
                        </Box>
                      </ClickAwayListener>
                    </Popper>
                  </>
                }
              />
              <Box sx={{ height: "100px" }}>
                {/* Spacer to prevent content cutoff */}
              </Box>
            </Stack>
          </div>
        </Stack>
      </div>
      <StackedSnackbar ref={snackbarRef} autoHideDuration={3000} />

      {isLoading && <Loader overlay />}
      {!!invoiceModalOpen && (
        <QBInvoiceDetailModal
          invoiceId={invoiceModalOpen}
          open={!!invoiceModalOpen}
          selectedMonth={selectedMonth}
          onClose={(hadAction) => {
            if (hadAction) {
              handleRecentlyUpdatedInvoices([invoiceModalOpen]);
            }
            setInvoiceModalOpen(null);
          }}
          snackbarRef={snackbarRef}
          metaData={metaData}
        ></QBInvoiceDetailModal>
      )}

      {overagesModalOpen && (
        <SFOveragesModal
          open={overagesModalOpen}
          month={dayjs(selectedMonth).subtract(1, "month")}
          onClose={() => setOveragesModalOpen(false)}
        />
      )}

      {/* Bulk modals and confirmation dialogs */}
      {bulk.modals}
      {single.modals}
    </>
  );
};
